import React, { forwardRef, useCallback, useRef, useEffect, useState } from 'react'
import { Mention } from 'react-mentions'
import type { ClientDTO, CompanyDTO } from '@/features/chat-bot/model'

import { MentionsRoot, StyledMentionsInput, SuggestionsPortal } from './styles'

interface IProps {
    name: string
    label?: string
    value?: string | number
    isError?: boolean
    minRows?: number
    maxRows?: number
    onChange?: (e: React.ChangeEvent<HTMLTextAreaElement> | string | undefined) => void
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
    isSuccess?: boolean
    fullWidth?: boolean
    className?: string
    placeholder?: string
    transparentInput?: boolean
    hideBorder?: boolean
    clientList?: ClientDTO[]
    companyList?: CompanyDTO[]
    lineHeightRem?: number
    onClientSelect?: (clientId: string) => void
    onCompanySelect?: (companyId: string) => void
    // When provided, suggestions are fetched on demand (debounced) using the
    // typed query instead of filtering the static `clientList`/`companyList`.
    onSearchClients?: (query: string) => Promise<ClientDTO[]>
    onSearchCompanies?: (query: string) => Promise<CompanyDTO[]>
    searchDebounceMs?: number
}

type MentionsChangeHandler = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
    newValue: string
) => void

interface SuggestionItem {
    id: string
    display: string
    clientName?: string
}

type DataCallback = (data: SuggestionItem[]) => void

export const TextAreaMentionsInputField = forwardRef<HTMLTextAreaElement, IProps>(
    (
        {
            name,
            label,
            value,
            isError = false,
            minRows,
            maxRows,
            onChange,
            onKeyDown,
            isSuccess = false,
            fullWidth = false,
            className,
            placeholder,
            transparentInput = false,
            hideBorder = true,
            lineHeightRem = 1.25,
            clientList = [],
            companyList = [],
            onClientSelect,
            onCompanySelect,
            onSearchClients,
            onSearchCompanies,
            searchDebounceMs = 300
        },
        ref
    ) => {
        const min = minRows ?? 3
        const max = maxRows ?? 8
        const lineHeightPx = lineHeightRem * 16

        const portalHostRef = useRef<HTMLDivElement | null>(null)
        const [showAbove, setShowAbove] = useState(false)
        const mentionsRootRef = useRef<HTMLDivElement | null>(null)

        const combinedRef = useCallback(
            (node: HTMLTextAreaElement | null) => {
                if (typeof ref === 'function') ref(node as any)
                else if (ref) (ref as any).current = node
            },
            [ref]
        )

        useEffect(() => {
            const checkPosition = () => {
                if (mentionsRootRef.current) {
                    const rect = mentionsRootRef.current.getBoundingClientRect()
                    const spaceBelow = window.innerHeight - rect.bottom
                    const spaceAbove = rect.top

                    setShowAbove(spaceBelow < 200 && spaceAbove > spaceBelow)
                }
            }

            checkPosition()
            window.addEventListener('scroll', checkPosition, true)
            window.addEventListener('resize', checkPosition)

            return () => {
                window.removeEventListener('scroll', checkPosition, true)
                window.removeEventListener('resize', checkPosition)
            }
        }, [])

        const handleKeyDownInternal = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter') {
                if (e.shiftKey) {
                    onKeyDown?.(e)
                    return
                }
                e.preventDefault()
                onKeyDown?.(e)
                const form = e.currentTarget.form
                form?.requestSubmit()
                return
            }

            onKeyDown?.(e)
        }

        const handleChange: MentionsChangeHandler = (_e, newValue) => {
            onChange?.(newValue)
        }

        const clientsData: SuggestionItem[] = clientList.map((c) => ({
            id: c.id,
            display: c.name
        }))
        const companiesData: SuggestionItem[] = companyList.map((c) => ({
            id: c.id,
            display: c.name,
            clientName: c.client.name
        }))

        // Debounced, on-demand suggestion providers. `react-mentions` accepts a
        // function for the `data` prop: it is called with the current query and a
        // callback to resolve the matching suggestions. We debounce the network
        // request and resolve the callback once results arrive.
        const clientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
        const companyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

        useEffect(() => {
            return () => {
                if (clientTimerRef.current) clearTimeout(clientTimerRef.current)
                if (companyTimerRef.current) clearTimeout(companyTimerRef.current)
            }
        }, [])

        const searchClientsData = useCallback(
            (query: string, resolve: DataCallback) => {
                if (!onSearchClients) return
                if (clientTimerRef.current) clearTimeout(clientTimerRef.current)
                clientTimerRef.current = setTimeout(() => {
                    onSearchClients(query)
                        .then((clients) => {
                            resolve(clients.map((c) => ({ id: c.id, display: c.name })))
                        })
                        .catch((_err: unknown) => {
                            resolve([])
                        })
                }, searchDebounceMs)
            },
            [onSearchClients, searchDebounceMs]
        )

        const searchCompaniesData = useCallback(
            (query: string, resolve: DataCallback) => {
                if (!onSearchCompanies) return
                if (companyTimerRef.current) clearTimeout(companyTimerRef.current)
                companyTimerRef.current = setTimeout(() => {
                    onSearchCompanies(query)
                        .then((companies) => {
                            resolve(
                                companies.map((c) => ({
                                    id: c.id,
                                    display: c.name,
                                    clientName: c.client.name
                                }))
                            )
                        })
                        .catch((_err: unknown) => {
                            resolve([])
                        })
                }, searchDebounceMs)
            },
            [onSearchCompanies, searchDebounceMs]
        )

        const textValue = String(value ?? '')

        const renderCompanySuggestion = (
            suggestion: { id: string; display: string; clientName?: string }
        ) => {
            return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontWeight: '500' }}>{suggestion.display}</div>
                    {suggestion.clientName && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                            {suggestion.clientName}
                        </div>
                    )}
                </div>
            )
        }

        const customSuggestionsContainer = useCallback(
            (children: React.ReactNode) => {
                if (!children) return null

                return (
                    <div
                        style={{
                            position: 'absolute',
                            [showAbove ? 'bottom' : 'top']: '100%',
                            left: 0,
                            zIndex: 1000,
                            marginTop: showAbove ? 0 : '4px',
                            marginBottom: showAbove ? '4px' : 0,
                            minWidth: '200px',
                        }}
                    >
                        {children}
                    </div>
                )
            },
            [showAbove]
        )

        const mentionsStyle = {
            control: {
                backgroundColor: 'transparent',
                fontSize: 16,
                fontWeight: 'normal',
                minHeight: `${min * lineHeightPx}px`,
                maxHeight: `${max * lineHeightPx}px`

            },
            highlighter: {
                overflowY: 'auto',
                maxHeight: `${max * lineHeightPx}px`,
                minHeight: `${min * lineHeightPx}px`,
            },
            '&multiLine': {
                highlighter: {
                    boxSizing: 'border-box',
                    padding: 9,
                    border: 'none',
                    outline: 'none'
                },
                input: {
                    padding: 9,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    overflowY: 'auto',
                    maxHeight: `${max * lineHeightPx}px`,
                    minHeight: `${min * lineHeightPx}px`
                }
            },
            suggestions: {
                list: {
                    backgroundColor: 'white',
                    border: '1px solid rgba(0,0,0,0.15)',
                    borderRadius: "4px",
                    fontSize: 14,
                    overflowY: 'auto',
                    maxHeight: `200px`,
                    minWidth: `200px`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
                item: {
                    padding: '8px 12px',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                    '&focused': {
                        backgroundColor: '#cee4e5'
                    }
                }
            }
        } as const

        return (
            <MentionsRoot
                ref={mentionsRootRef}
                $isError={isError}
                $isSuccess={isSuccess}
                $fullWidth={fullWidth}
                $transparentInput={transparentInput}
                $hideBorder={hideBorder}
                className={className}
            >
                <StyledMentionsInput
                    value={textValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDownInternal}
                    placeholder={placeholder ?? ' '}
                    style={mentionsStyle}
                    inputRef={combinedRef}
                    rows={min}
                    allowSpaceInQuery
                    suggestionsPortalHost={portalHostRef.current ?? undefined}
                    customSuggestionsContainer={customSuggestionsContainer}
                    allowSuggestionsAboveCursor={showAbove}
                >
                    <Mention
                        trigger='@'
                        data={onSearchClients ? searchClientsData : clientsData}
                        displayTransform={(_id: string | number, display: string) => `@${display}`}
                        markup='@[__display__](client-id:__id__)'
                        onAdd={(id: string | number) => {
                            onClientSelect?.(String(id))
                        }}
                        style={{
                            backgroundColor: "#ABD4D2",
                            color: "#26A199",
                            borderRadius: "4px",
                        }}
                    />

                    <Mention
                        trigger='#'
                        data={onSearchCompanies ? searchCompaniesData : companiesData}
                        displayTransform={(_id: string | number, display: string) => `#${display}`}
                        markup='#[__display__](company-id:__id__)'
                        onAdd={(id: string | number) => {
                            onCompanySelect?.(String(id))
                        }}
                        renderSuggestion={renderCompanySuggestion}
                        style={{
                            backgroundColor: "#ABD4D2",
                            color: "#26A199",
                            borderRadius: "4px",
                        }}
                    />
                </StyledMentionsInput>
                <SuggestionsPortal ref={portalHostRef} />
            </MentionsRoot>
        )
    }
)

TextAreaMentionsInputField.displayName = 'TextAreaMentionsInputField'

